<template>
  <div class="flex-auto ce-formulationDetailPage">
    <main class="ce-container">
      <div
        v-if="backlink"
        class="ce-backCta">
        <a :href="backlink">
          <fa-icon icon="long-arrow-alt-left" />
          {{ $t('productDetail.backCta') }}
        </a>
      </div>

      <div class="flex justify-between">
        <div>
          <div class="flex">
            <div
              v-if="previewImage"
              class="ce-shadeImage">
              <div
                class="ce-backgroundImageContainer"
                :style="{'background-image': `url(${previewImage.url})`}"
                @click="showPreviewImage">
                <div>
                  <fa-icon icon="search-plus" />
                </div>
              </div>
            </div>

            <ColorSpaceBadge
              v-else-if="color"
              :color="color" />

            <div class="flex flex-column justify-center">
              <h1>
                {{ formulation.title }}
              </h1>
            </div>
          </div>

          <div class="flex mt3 ce-pageActions no-print">
            <button
              class="ce-btn-as-link"
              @click.prevent="triggerPrinting">
              <fa-icon
                icon="print" />
              <span>{{ $t('productDetail.printCta') }}</span>
            </button>

            <button
              class="ce-btn-as-link"
              @click.prevent="savePage">
              <fa-icon
                :icon="['far', 'file-pdf']" />
              <span>{{ $t('productDetail.savePageCta') }}</span>
            </button>

            <FavoriteAction
              :shade="shade"
              :formulation="formulation"
              show-label />
          </div>
        </div>

        <div
          v-if="shouldShowQrCode"
          class="ce-qrContainer">
          <div>
            <a
              href="#"
              @click.prevent="showQR()">
              <img
                :src="shade.qrCode"
                height="100">
            </a>
            <div>
              <fa-icon icon="mobile-alt" />
              {{ $t('productDetail.qr.info') }}
              <fa-icon icon="long-arrow-alt-right" />
            </div>
          </div>
          <div class="text-right">
            <a
              href="https://colormaterials.sunchemical.com/pigment-viewer-mobile-app/"
              target="_blank"
              class="ms-2 inline-block mt2"
              v-html="$t('productDetail.qr.cta')" />
          </div>
        </div>
      </div>

      <div class="pt3">
        <h2>
          {{ $t('formulationRepository.detailPage.description.headline') }}
        </h2>
        <div
          v-if="formulation.description"
          v-html="formulation.description" />
      </div>

      <div class="colWrapper flex-wrap">
        <DetailsTable />

        <FormulationIngredients />

        <FormulationAttachments />
      </div>

      <SimpleGallery
        :images="galleryImages"
        :index="galleryIndex"
        @close="galleryIndex = null" />
    </main>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import FormulationAttachments from 'components/formulation-repo/detail-page/FormulationAttachments.vue';
import FormulationIngredients from 'components/formulation-repo/detail-page/FormulationIngredients.vue';
import DetailsTable from 'components/formulation-repo/detail-page/DetailsTable.vue';
import FavoriteAction from 'components/formulation-repo/FavoriteAction.vue';
import ColorSpaceBadge from 'components/ColorSpaceBadge.vue';
import SimpleGallery from 'components/SimpleGallery.vue';

export default {
  data() {
    return {
      galleryIndex: null,
      hideShadeImage: false,
    };
  },
  created() {
    this.loadShadeImage();
  },
  components: {
    ColorSpaceBadge,
    SimpleGallery,
    FormulationAttachments,
    FormulationIngredients,
    DetailsTable,
    FavoriteAction,
  },
  computed: {
    ...mapState(['fieldManifest']),
    ...mapState('user', ['activeUser']),
    ...mapState(['formulation', 'shade', 'backlink']),
    ...mapState('generic', ['links', 'disableQrCodes', 'services']),
    shouldShowQrCode() {
      return this.shade.qrCode && !this.disableQrCodes;
    },
    pdfDownloadUrl() {
      let url = `${this.services.backend.url}/util/download/formulation/${this.formulation.id}`;

      if (this.activeUser) {
        url += `?token=${this.activeUser.token}`;
      }

      return url;
    },
    color() {
      if (this.shade.color) {
        return this.fieldManifest.color_space.optionsConfig.find((c) => c.code === this.shade.color);
      }

      return null;
    },
    previewImage() {
      if (this.shade.imgUrl && !this.hideShadeImage) {
        return {
          url: this.shade.imgUrl,
          id: 'shadeImage'
        };
      }

      if (this.shade.stackPreviewImage?.url) {
        return {
          url: this.shade.stackPreviewImage.url,
          id: 'stackPreviewImage'
        };
      }

      return null;
    },
    galleryImages() {
      const imgs = [];

      if (this.shade.imgUrl) {
        imgs.push({
          src: this.shade.imgUrl,
          id: 'shadeImage'
        });
      }

      if (this.shade.stackPreviewImage?.url) {
        imgs.push({
          src: this.shade.stackPreviewImage.url,
          id: 'stackPreviewImage'
        });
      }

      if (this.shouldShowQrCode) {
        imgs.push({
          title: this.$t('productDetail.qr.info'),
          src: this.shade.qrCode,
          id: 'qrCode'
        });
      }

      return imgs;
    }
  },
  methods: {
    loadShadeImage() {
      if (this.shade.imgUrl) {
        const shadeImg = new Image();
        // hide image if load failed
        shadeImg.onerror = () => {
          this.hideShadeImage = this.shade.imgUrl;
        };
        shadeImg.src = this.shade.imgUrl;
      }
    },
    showGalleryImageById(id) {
      const imageIndex = this.galleryImages.findIndex((i) => i.id === id);
      if (imageIndex > -1) {
        this.galleryIndex = imageIndex;
      }
    },
    showQR() {
      this.showGalleryImageById('qrCode');
    },
    showPreviewImage() {
      if (this.previewImage) {
        this.showGalleryImageById(this.previewImage.id);
      }
    },
    triggerPrinting() {
      this.$trackEvent(
        'Formulation',
        'Print',
        {
          id: this.formulation.id
        }
      );
      window.open(this.pdfDownloadUrl);
    },
    savePage() {
      this.$trackEvent(
        'Formulation',
        'Save as PDF',
        {
          id: this.formulation.id
        }
      );

      const char = this.pdfDownloadUrl.indexOf('?') > -1 ? '&' : '?';
      location.href = `${this.pdfDownloadUrl}${char}download=1`;
    }
  }
};
</script>

<style lang="less">
@import '~styles/includes/base';
@import "~styles/includes/color-badge";

.ce-formulationDetailPage {
  main {
    .ce-shadeImage {
      .flex-none;
      margin-right: @space-4;

      .ce-backgroundImageContainer {
        background-repeat: no-repeat;
        background-position: center center;
        background-size: cover;
        width: 140px;
        max-width: 100%;
        height: 80px;
        border-radius: @border-radius-lg;
        display: flex;
        align-items: flex-end;
        justify-content: flex-end;

        &:hover {
          cursor: pointer;
        }

        div {
          padding: @space-1 @space-2;
          background: @color-light;
          color: @color-mono-100;
        }
      }
    }

    .ce-colorBadge-holder {
      .flex-none;
      margin-right: @space-4;

      .ce-colorBadge {
        margin: 0;
        width: 140px;
        height: 80px;
        border-radius: @border-radius-lg;
      }
    }

    h1 {
      margin: 0;
      .ms(2);

      @media screen and (min-width: @breakpoint-xxs) {
        .ms(3);
      }

      @media screen and (min-width: @breakpoint-xs) {
        .ms(4);
      }

      @media screen and (min-width: @breakpoint-sm) {
        .ms(5);
      }

      @media screen and (min-width: @breakpoint-lg) {
        .ms(6);
      }
    }

    h2 {
      text-transform: uppercase;
      .ms(1);
      margin-bottom: @space-3;
    }

    .colWrapper {
      padding-bottom: @space-4;

      > div {
        width: 100%;
        padding: @space-4 0;
      }

      @media screen and (min-width: @breakpoint-lg) {
        display: flex;
        margin: 0 -@space-5;

        > div {
          width: 50%;
          padding: @space-5;
        }
      }
    }

    table.ce-dataTable {
      border-spacing: 0;
      width: 100%;

      th {
        text-align: left;
        padding: @space-2;
        line-height: 1.5;
      }

      tr {
        td {
          padding: @space-2;
          line-height: 1.5;
        }
      }
    }

    .ce-pageActions {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      .mxn3;

      > * {
        margin: @space-2 @space-3;
      }

      > a, > button.ce-btn-as-link {
        color: @color-text-base;
        .ms(-1);

        &:hover {
          color: @color-light;
        }

        &.disabled {
          cursor: default;
          color: @color-text-base;

          .fa-layers, .ce-pageActionLabel {
            opacity: 0.5;
          }
        }
      }

      a {
        &.ce-colorSuccess {
          color: @color-success;
        }
      }
    }

    .ce-qrContainer {
      @media only screen and (max-width: @breakpoint-sm) {
        display: none;
      }

      > div:first-child {
        text-align: center;
        display: flex;
        flex-direction: row-reverse;
        align-items: center;

        a {
          img {
            border: 1px solid @color-mono-70;
            display: inline-block;
            border-radius: @border-radius;
          }
        }

        a:last-of-type {
          display: block;
          max-width: 120px;
        }

        > div {
          max-width: 150px;
          font-weight: bold;
          transform: rotateZ(-15deg) translate(15px, -40px);
        }
      }
    }
  }
}
</style>
